import { Segmented, Typography } from "antd";
import { useReduxMetricDropdown } from "./useReduxMetricDropdown";
import { aggOptionsFor, allowedAggsFromDropdown, coerceAgg } from "./metricAggregations";
import { MetricSelect } from "./MetricSelect";
import {
  CountIncludePicker,
  countSourceFromMetricId,
  defaultCountSpec,
  type CountSpec,
} from "./CountIncludePicker";

const { Text } = Typography;

export interface MetricDropdownStepProps {
  selectedMetricId: string | null;
  metricAgg: "avg" | "sum" | "best";
  onSelectMetric: (id: string) => void;
  onAggChange: (agg: "avg" | "sum" | "best") => void;
  /** Present only where the widget type accepts a count. */
  countSpec?: CountSpec;
  onCountSpecChange?: (spec: CountSpec | undefined) => void;
  /** Present only where the widget type accepts a helmet metric. */
  includeHelmet?: boolean;
}

export function MetricDropdownStep({
  selectedMetricId,
  metricAgg,
  onSelectMetric,
  onAggChange,
  countSpec,
  onCountSpecChange,
  includeHelmet = false,
}: MetricDropdownStepProps) {
  const { metricDropdown } = useReduxMetricDropdown({ includeCounts: true, includeHelmet });
  const allowedAggs = allowedAggsFromDropdown(metricDropdown, selectedMetricId);

  return (
    <div className="awm-step-body">
      <MetricSelect
        metricDropdown={metricDropdown}
        value={selectedMetricId}
        onChange={(id) => {
          onSelectMetric(id);
          // A helmet count has no best: the backend rejects it on save.
          const allowed = allowedAggsFromDropdown(metricDropdown, id);
          if (allowed && coerceAgg(metricAgg, allowed) !== metricAgg) onAggChange(coerceAgg(metricAgg, allowed));
          if (!onCountSpecChange) return;
          // The include-list, the match mode and the injury reading all belong to one
          // source, so a change of metric replaces the spec rather than carrying it.
          const source = countSourceFromMetricId(id);
          onCountSpecChange(source ? defaultCountSpec(source) : undefined);
          // A count has no average or best; the backend accepts only the total.
          // Leaving one restores the default: the "sum" was forced by the count, not
          // chosen by the author, so it must not follow them to the next metric.
          if (source) onAggChange("sum");
          else if (countSpec) onAggChange("avg");
        }}
      />
      {countSpec && onCountSpecChange && (
        <div className="awm-labeled-row">
          <Text className="awm-labeled-row-label">Counting</Text>
          <CountIncludePicker spec={countSpec} onChange={onCountSpecChange} />
        </div>
      )}
      {selectedMetricId != null && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text style={{ fontSize: 12 }}>Aggregation</Text>
          {countSpec ? (
            <>
              <span className="awm-count-fixed-agg">Total</span>
              <Text type="secondary" style={{ fontSize: 11 }}>
                a count has no average
              </Text>
            </>
          ) : (
            <Segmented
              size="small"
              options={aggOptionsFor(allowedAggs)}
              value={metricAgg}
              onChange={(v) => onAggChange(v as "avg" | "sum" | "best")}
            />
          )}
        </div>
      )}
    </div>
  );
}
